import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { HealthCheckDetailDto, HealthCheckStatus } from '@jordan/types';
import { Alert, Breadcrumb, Button, PageHeader, Spinner } from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { apiDelete, apiGet } from '../../lib/api';
import { cloudSidebar } from './cloudSidebar';

const STATUS: Record<HealthCheckStatus, { label: string; variant: 'success' | 'danger' | 'secondary'; icon: string }> = {
  healthy: { label: 'Healthy', variant: 'success', icon: 'ph-check-circle' },
  unhealthy: { label: 'Unhealthy', variant: 'danger', icon: 'ph-x-circle' },
  unknown: { label: 'Unknown', variant: 'secondary', icon: 'ph-question' },
};

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="d-flex py-2 border-bottom">
      <span className="text-muted" style={{ width: 200 }}>
        {label}
      </span>
      <span className="fw-semibold">{value}</span>
    </div>
  );
}

export function CloudHealthCheckDetailPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { id = '' } = useParams();

  const [hc, setHc] = useState<HealthCheckDetailDto | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setErr(null);
    apiGet<HealthCheckDetailDto>(`/api/cloud/health-checks/${encodeURIComponent(id)}`)
      .then(setHc)
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : 'Could not load the health check.'))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);

  const remove = async () => {
    if (!hc) return;
    if (!window.confirm(`Delete health check ${hc.name || hc.id}? This can’t be undone.`)) return;
    try {
      await apiDelete(`/api/cloud/health-checks/${encodeURIComponent(id)}`);
      navigate('/cloud/health-checks');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not delete the health check.');
    }
  };

  const title = hc?.name || hc?.id || 'Health check';
  const s = hc ? STATUS[hc.status] : null;

  return (
    <AppChrome secondarySidebar={cloudSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Health Check - <span className="fw-normal">{title}</span>
          </>
        }
        breadcrumb={
          <Breadcrumb
            items={[
              { icon: 'ph-house', href: '/' },
              { label: 'Cloud Management' },
              { label: 'Health Check', href: '/cloud/health-checks' },
              { label: title },
            ]}
          />
        }
        actions={
          <div className="d-flex gap-2">
            <Button variant="light" icon="ph-arrows-clockwise" disabled={loading} onClick={load}>
              Refresh
            </Button>
            {hc && (
              <Button variant="light" className="text-danger" icon="ph-trash" onClick={() => void remove()}>
                Delete
              </Button>
            )}
          </div>
        }
      />

      <div className="content">
        {err && (
          <Alert variant="warning" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}

        {hc === null && loading ? (
          <Spinner />
        ) : hc && s ? (
          <>
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body text-center">
                    <div className="text-muted">Current status</div>
                    <div className={`fs-2 my-2 text-${s.variant}`}>
                      <i className={s.icon} />
                    </div>
                    <span className={`badge bg-${s.variant} bg-opacity-25 text-${s.variant}`}>{s.label}</span>
                  </div>
                </div>
              </div>
              <div className="col-md-8">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-transparent">
                    <h6 className="mb-0 fw-semibold">Configuration</h6>
                  </div>
                  <div className="card-body py-0">
                    <Row label="Type" value={<span className="badge bg-light text-body border">{hc.type}</span>} />
                    <Row label="Endpoint" value={<span className="font-monospace">{hc.endpoint}</span>} />
                    {hc.fqdn && <Row label="Domain name" value={hc.fqdn} />}
                    {hc.ipAddress && <Row label="IP address" value={hc.ipAddress} />}
                    {hc.port != null && <Row label="Port" value={hc.port} />}
                    {hc.resourcePath && <Row label="Path" value={<span className="font-monospace">{hc.resourcePath}</span>} />}
                    {hc.searchString && <Row label="Search string" value={<span className="font-monospace">{hc.searchString}</span>} />}
                    <Row label="Request interval" value={hc.requestInterval == null ? '—' : `${hc.requestInterval}s`} />
                    <Row label="Failure threshold" value={hc.failureThreshold ?? '—'} />
                    <Row label="Health check ID" value={<span className="font-monospace">{hc.id}</span>} />
                  </div>
                </div>
              </div>
            </div>

            {hc.lastFailureReason && (
              <Alert variant="warning" icon="ph-warning">
                <span className="fw-semibold">Last failure: </span>
                {hc.lastFailureReason}
              </Alert>
            )}

            <div className="card border-0 shadow-sm">
              <div className="card-header bg-transparent">
                <h6 className="mb-0 fw-semibold">Health checkers ({hc.observations.length})</h6>
              </div>
              <div className="table-responsive">
                <table className="table mb-0">
                  <thead>
                    <tr>
                      <th>Region</th>
                      <th>Checker IP</th>
                      <th>Status</th>
                      <th>Checked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hc.observations.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-4">No observations reported.</td>
                      </tr>
                    ) : (
                      hc.observations.map((o, i) => {
                        const ok = /^success/i.test(o.status);
                        return (
                          <tr key={i}>
                            <td className="fw-semibold">{o.region}</td>
                            <td className="font-monospace">{o.ipAddress ?? '—'}</td>
                            <td className={ok ? 'text-success' : 'text-danger'}>{o.status}</td>
                            <td className="text-muted">{o.checkedTime ? new Date(o.checkedTime).toLocaleString() : '—'}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </AppChrome>
  );
}
